import { useLoggedInAs, usePostHog } from "shared/hooks";
import { PeopleContext, Person } from "./PeopleContext";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  EnumLeadStatus,
  EnumOwnerType,
  PersonDetailsDocument,
  SortFindManyPeopleInput,
  useLeadRemoveByIdMutation,
  useLeadUpdateByIdMutation,
  usePeopleCountLazyQuery,
  usePeopleManySimpleQuery,
} from "gql/generated";
import { NetworkStatus } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useToastContext } from "@ttc3k/trekker";
import _ from "lodash";

const PEOPLE_PER_FETCH = 500;

type PeopleContextProviderProps = {
  // When NOP is true, the array of people will NOT be auto-loaded
  // and the automatic fetchMore will NOT be called
  // - NOP is TRUE for the PeopleApp, Storefront/PeopleView, and Adminland/Directories/People
  //   because each of these pages uses pagination instead of infinite scrolling
  // This is also useful when using this context on staff pages where we're
  // already loading the staff members and we don't care about leads/customers
  NOP?: boolean;
  FETCH_LIMIT?: number;
  entityOverride?: { entityId: string; type: EnumOwnerType };
};

export const PeopleContextProvider = ({
  NOP,
  FETCH_LIMIT = PEOPLE_PER_FETCH,
  entityOverride,
  children,
}: PeopleContextProviderProps & PropsWithChildren) => {
  const loggedInAs = useLoggedInAs();
  const entity = entityOverride ?? loggedInAs;
  const { t } = useTranslation("people");
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();
  const { trackPostHogException } = usePostHog();

  const showSuccessToast = useCallback(
    (message: string) => {
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: message,
      });
    },
    [toastFactory, tCore],
  );

  const showErrorToast = useCallback(
    (message: string) => {
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: message,
      });
    },
    [toastFactory, tCore],
  );

  /**
   * Lead mutators
   * - While leads aren't exactly the PEOPLE model, the only place to view a lead is from
   *   the various People pages throughout the app (Adminland/People, Storefront/People, Organization/People)
   * - So, for now, we'll manage marking a lead as read/unread and removing a lead from the person's leads array
   *   here in the PeopleContextProvider. This will make it easier to share this logic between the various People pages.
   *   But in the future, this could be moved to a separate context provider for managing leads.
   * - These mutators will refetch the PersonDetailsDocument query to update the underlying PersonDetailsModal data
   */
  const [leadRemoveById, { loading: removeLeadByIdLoading }] =
    useLeadRemoveByIdMutation({ refetchQueries: [PersonDetailsDocument] });
  const [leadUpdateById, { loading: updateLeadStatusByIdLoading }] =
    useLeadUpdateByIdMutation({ refetchQueries: [PersonDetailsDocument] });

  // Store the full people list for all loaded pages
  // Note that the `people` array is used by the PeopleTagsContextProvider when tags are updated
  // (split, deleted, merged) ... it updates the information in place to avoid having to refetch
  // So, even when were in pagination mode, we need to fill the people array when new people are loaded
  const [people, setPeople] = useState<Person[]>([]);
  // The map to store a group of people for each page (cached for pagination)
  const [pagedPeople, setPagedPeople] = useState(new Map<number, Person[]>());
  // The people for the current page (cached for pagination)
  const [peopleForPage, setPeopleForPage] = useState<Person[]>([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPeople, setTotalPeople] = useState(0);

  const [hasMore, setHasMore] = useState(NOP ? false : true);
  const [initialLoad, setInitialLoad] = useState(NOP ? false : true);

  const lastSearchRef = useRef<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryBuilder: Record<string, any> | null;
    sort: SortFindManyPeopleInput;
  } | null>(null);

  // Query to get the total number of people
  // - This will include logic to count with the selected filters and query builder
  const [getPeopleCount] = usePeopleCountLazyQuery();

  // When NOP === TRUE, the initial query here is skipped
  // and the automatic fetchMore will NOT be called
  const {
    error,
    networkStatus,
    fetchMore,
    refetch: refetchPeople,
  } = usePeopleManySimpleQuery({
    variables: {
      filter: {
        visibleTo: [
          {
            entity: {
              entityId: entity?.entityId,
              type: entity?.type,
            },
          },
        ],
      },
      limit: FETCH_LIMIT,
      skip: 0,
    },
    skip: NOP || !entity,
    onCompleted: data => {
      // Since onCompleted will always be called, in NOP mode, we want to do nothing
      if (NOP) return;

      // If we're refetching because PeopleManySimpleDocument was included in a refetchQueries array,
      // we need to reset the people array
      // NOTE: networkStatus === NetworkStatus.refetch ONLY on the first refetch, so subsequent calls in
      // the batch process will not reset the people array
      if (networkStatus === NetworkStatus.refetch) {
        setPeople([]);
      }

      const newPeople = data.peopleManySimple ?? [];
      const hasMore = newPeople.length === FETCH_LIMIT;

      setHasMore(hasMore);
      setInitialLoad(false);
      setPeople(prevPeople => [...prevPeople, ...newPeople]);
    },
    notifyOnNetworkStatusChange: true,
  });

  /**
   * Handles automatic refetching of people when the hasMore state is true
   * - When NOP === TRUE, this does nothing
   */
  useEffect(() => {
    // Avoid calling fetchMore on the initial load
    if (!NOP && hasMore && !initialLoad) {
      fetchMore({
        variables: {
          skip: people.length,
        },
        updateQuery: (_, { fetchMoreResult }) => {
          if (!fetchMoreResult?.peopleManySimple?.length) {
            return { peopleManySimple: [] };
          }

          // Since we're using no-cache, we need to manually merge the results
          return {
            peopleManySimple: fetchMoreResult.peopleManySimple,
          };
        },
      });
    }
  }, [people.length, hasMore, initialLoad, fetchMore, NOP]);

  /**
   * Searches for people
   * - Called when running an initial search via react-querybuilder OR
   *   typing in a name/email in the search box OR when clicking on a tag
   * - The people array, currentPage, totalPeople, and peopleForPage state will be reset
   * @param queryBuilder - The query builder to use to filter the people
   * @param sort - The sort to use to sort the people
   * @returns the people that match the search
   */
  const searchPeople = useCallback(
    async (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryBuilder: Record<string, any> | null,
      sort: SortFindManyPeopleInput,
    ) => {
      if (!entity) return;

      lastSearchRef.current = { queryBuilder, sort };

      const variables = {
        filter: {
          visibleTo: [
            {
              entity: {
                entityId: entity?.entityId,
                type: entity?.type,
              },
            },
          ],
        },
        queryBuilder: queryBuilder ? JSON.stringify(queryBuilder) : "{}",
      };

      setPeopleLoading(true);
      const { data: countData } = await getPeopleCount({ variables });
      setTotalPeople(countData?.peopleCount ?? 0);

      const { data } = await refetchPeople({
        ...variables,
        sort,
        skip: 0,
        limit: FETCH_LIMIT,
      });

      setCurrentPage(1);
      // Add the new people to the people array
      setPeople(data.peopleManySimple);
      setPagedPeople(new Map([[1, data.peopleManySimple]]));
      setPeopleForPage(data.peopleManySimple);
      setPeopleLoading(false);
    },
    [FETCH_LIMIT, entity, refetchPeople, getPeopleCount],
  );

  const refreshPeopleList = useCallback(async () => {
    if (!entity) return;
    const last = lastSearchRef.current;
    if (!last) return;
    await searchPeople(last.queryBuilder, last.sort);
  }, [entity, searchPeople]);

  /**
   * Fetches a page of people using the same variables as the searchPeople function
   */
  const fetchPage = useCallback(
    async (page: number) => {
      setCurrentPage(page);

      const pagePeople = pagedPeople.get(page);
      if (pagePeople) {
        setPeopleForPage(pagePeople);
      } else {
        const skip = (page - 1) * FETCH_LIMIT;
        setPeopleLoading(true);
        const { data } = await fetchMore({
          variables: { skip },
        });
        pagedPeople.set(page, data.peopleManySimple);
        // Add the new people to the people array
        setPeople(prev => [...prev, ...data.peopleManySimple]);
        setPeopleForPage(data.peopleManySimple);
        setPeopleLoading(false);
      }
    },
    [FETCH_LIMIT, pagedPeople, fetchMore],
  );

  /**
   * Sets the lead status to DELETED and removes the lead from the person's leads array
   * - In the SimplePersonManyTC type, this lead may be included in the unreadLeads array,
   *   so we need to remove it from that array as well
   * @param personId - The id of the person to remove the lead from
   * @param leadId - The id of the lead to remove
   * @returns true if the lead was removed, false otherwise
   */
  const removeLeadById = useCallback(
    async (personId: string, leadId: string) => {
      if (!leadId || !personId) return false;

      try {
        const { data, errors } = await leadRemoveById({
          variables: {
            id: leadId,
          },
        });
        if (errors) {
          throw new Error(errors[0].message);
        }
        if (!data?.leadRemoveById) {
          throw new Error("Failed to remove lead");
        }
        // Remove the lead from the person's unreadLeads array (if it exists in that array)
        setPeople(prev =>
          prev.map(person =>
            person._id !== personId
              ? person
              : {
                  ...person,
                  unreadLeads:
                    person.unreadLeads?.filter(
                      lead => lead?.leadId !== leadId,
                    ) ?? [],
                },
          ),
        );
        showSuccessToast(t("LEAD_MODAL.MESSAGES.REMOVAL_SUCCESS"));
        return true;
      } catch (error) {
        trackPostHogException(error, {
          fileName: "PeopleContextProvider.tsx",
          method: "removeLeadById",
          message: "Error removing lead",
        });
        showErrorToast(t("LEAD_MODAL.MESSAGES.REMOVAL_ERROR"));
      }
      return false;
    },
    [
      t,
      leadRemoveById,
      setPeople,
      showSuccessToast,
      showErrorToast,
      trackPostHogException,
    ],
  );

  /**
   * Sets the lead status to READ/UNREAD and updates the person's unreadLeads array
   * - If the status was changed to READ, we need to remove the lead from the unreadLeads array
   * - If the status was changed to UNREAD, we need to add the lead to the unreadLeads array
   * @param personId - The id of the person to update the lead status for
   * @param leadId - The id of the lead to update the status for
   * @param status - The new status to set the lead to
   * @returns true if the lead status was updated, false otherwise
   */
  const updateLeadStatusById = useCallback(
    async (personId: string, leadId: string, status: EnumLeadStatus) => {
      if (!leadId || !personId) return false;

      try {
        const { data, errors } = await leadUpdateById({
          variables: {
            id: leadId,
            record: { status },
          },
        });
        if (errors) {
          throw new Error(errors[0].message);
        }
        if (!data?.leadUpdateById) {
          throw new Error("Failed to update lead");
        }
        // Update the lead status in the person's unreadLeads array (if it exists in that array)
        // - If the status was changed to READ, we need to remove the lead from the unreadLeads array
        // - If the status was changed to UNREAD, we need to add the lead to the unreadLeads array
        setPeople(prev =>
          prev.map(person =>
            person._id !== personId
              ? person
              : {
                  ...person,
                  unreadLeads:
                    status === EnumLeadStatus.Read
                      ? // Remove the item since it is read
                        (person.unreadLeads?.filter(
                          lead => lead?.leadId !== leadId,
                        ) ?? [])
                      : // Add the item since it is unread
                        _.orderBy(
                          [
                            ...(person.unreadLeads ?? []),
                            {
                              leadId,
                              date: data.leadUpdateById?.record?.createdDateISO,
                            },
                          ],
                          "date",
                          "asc",
                        ),
                },
          ),
        );
        showSuccessToast(t(`LEAD_MODAL.MESSAGES.MARK_AS_${status}_SUCCESS`));
        return true;
      } catch (error) {
        trackPostHogException(error, {
          fileName: "PeopleContextProvider.tsx",
          method: "updateLeadStatusById",
          message: "Error updating lead status",
        });
        showErrorToast(t(`LEAD_MODAL.MESSAGES.MARK_AS_${status}_ERROR`));
      }
      return false;
    },
    [
      t,
      leadUpdateById,
      setPeople,
      showSuccessToast,
      showErrorToast,
      trackPostHogException,
    ],
  );

  const values = useMemo(
    () => ({
      people,
      updatePeople: setPeople,
      peopleForPage,
      currentPage,
      totalPeople,
      fetchPage,
      searchPeople,
      hasMore,
      initialLoad,
      error,
      peopleLoading,
      removeLeadByIdLoading,
      removeLeadById,
      updateLeadStatusByIdLoading,
      updateLeadStatusById,
      refreshPeopleList,
    }),
    [
      people,
      setPeople,
      peopleForPage,
      currentPage,
      totalPeople,
      fetchPage,
      searchPeople,
      hasMore,
      initialLoad,
      error,
      peopleLoading,
      removeLeadByIdLoading,
      removeLeadById,
      updateLeadStatusByIdLoading,
      updateLeadStatusById,
      refreshPeopleList,
    ],
  );

  return (
    <PeopleContext.Provider value={values}>{children}</PeopleContext.Provider>
  );
};
